import '../../styles/pages/whiteLabel.scss';
import store from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import {
  setAccessToken,
  setIsAdminSession,
  setEmployeeIdentity,
} from '../../store/reducers/authSlice';
import { useNavigate } from 'react-router-dom';
import {
  fetchAppInit,
  getLastAppInitFailure,
  INIT_FORBIDDEN_MESSAGE,
} from '../../lib/api/app-init';
import { restoreSessionFromSeller } from '../../lib/api/session/restore-session-from-seller';
import { applySessionFromResponse } from '../../lib/api/session/apply-session';
import { fetchIdentity } from '../../lib/api/identity/identity-api';
import { submitLogToBugsnag } from '../../lib/api/log';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import moment from 'moment';
import { useEffect } from 'react';
import { HOME_ROUTE } from '../../lib/constants/route';
import localStorageHardReset from '../../lib/helper-functions/localStorageHardReset';
import { setPendingLoginError } from '../../onboardingFlow/login/pendingLoginError';

// Read the impersonation hand-off token from the URL fragment (preferred:
// admin-panel now delivers it as `#token=`, which never reaches servers, logs,
// or Referer) with a fallback to the legacy `?token=` query param for one
// release of compatibility. Reading + scrubbing happens SYNCHRONOUSLY on mount,
// before any await, so the seller JWT does not linger in the address bar or
// browser history.
const readAndScrubHandoffToken = (): string | null => {
  let token: string | null = null;

  const hash = window.location.hash?.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (hash) {
    token = new URLSearchParams(hash).get('token');
  }
  if (!token) {
    token = new URLSearchParams(window.location.search).get('token');
  }

  // Strip token from both the query string and the fragment immediately.
  try {
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname
    );
  } catch {
    // history API unavailable — non-fatal
  }

  return token;
};

export const PORTAL_LOGIN_FAILED_MESSAGE =
  'Sign-in failed. Please sign in again or contact support.';

const PortalLogin = () => {
  const navigate = useNavigate();
  const token_type = 'Bearer';

  useEffect(() => {
    // Synchronous: capture then scrub before any async work begins.
    const token = readAndScrubHandoffToken();
    localStorageHardReset();
    setTimeout(async () => {
      try {
        if (token && isNotEmptyString(token)) {
          store.dispatch(
            setAccessToken({
              sellerToken: token,
              type: token_type,
              expiresOn: moment().add(1, 'days'),
            })
          );

          // The admin "login as seller" handoff carries ONLY the legacy seller
          // JWT — no agency `emp_` key. The portal is employee-centric (every
          // /api/v1/me + /api/v1/agency/* call authenticates with the agency
          // key), so without it the whole portal 401s `invalid_token`. Re-mint
          // the agency session from the seller JWT (the SAME bridge a normal
          // login uses) and apply it so the agency surface authenticates and the
          // employee identity (brand selectors, RBAC) populates. Non-fatal: a
          // failure leaves the legacy seller-only view working.
          try {
            const agencySession = await restoreSessionFromSeller(token);
            if (agencySession) {
              applySessionFromResponse(agencySession);
              const identity = await fetchIdentity(
                agencySession.agency_api_key
              );
              if (identity) {
                store.dispatch(setEmployeeIdentity(identity));
              }
            }
          } catch (bridgeError) {
            submitLogToBugsnag(
              'error',
              `PortalLogin : agency session re-mint failed : ${bridgeError}`
            );
          }

          const isInitFetched = await fetchAppInit();
          if (isInitFetched) {
            store.dispatch(setIsAdminSession(true));
            navigate(HOME_ROUTE);
          } else {
            // fetchAppInit has already logged out and reset local state; the
            // message is set after that so the login page shows it.
            const failure = getLastAppInitFailure();
            submitLogToBugsnag(
              'error',
              `PortalLogin : app init failed (status ${
                failure?.status ?? 'none'
              })`
            );
            const loginError =
              failure?.status === 403
                ? INIT_FORBIDDEN_MESSAGE
                : PORTAL_LOGIN_FAILED_MESSAGE;
            setPendingLoginError(loginError);
            store.dispatch(
              setMessage({ message: loginError, messageType: 'error' })
            );
            navigate('/login');
          }
        } else {
          store.dispatch(
            setMessage({
              message: 'Authorization failed',
              messageType: 'error',
            })
          );
          console.log('PortalLogin : Token is empty or invalid.');
          submitLogToBugsnag(
            'error',
            `PortalLogin : Token is empty or invalid.`
          );
          navigate('/login');
        }
      } catch (error) {
        store.dispatch(
          setMessage({
            message: PORTAL_LOGIN_FAILED_MESSAGE,
            messageType: 'error',
          })
        );
        console.log('error', `PortalLogin : ${error}`);
        submitLogToBugsnag('error', `PortalLogin : ${error}`);
        setPendingLoginError(PORTAL_LOGIN_FAILED_MESSAGE);
        navigate('/login');
      }
    }, 1000);
  }, []);

  return (
    <div className='container'>
      <div className='loadingSpinner__roller'>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>
  );
};

export default PortalLogin;
